/**
 * Formatting utilities for video annotation and documentation.
 * Uses shared formatters to ensure consistency between documentation and actual output.
 */

import { TextAlignment, VideoData } from '@lumen5/luminary';

import { ASSET_TYPES } from '../constants';
import type {
    CaptionDescription,
    ColorAssetDescription,
    DocumentationExample,
    GroupAssetDescription,
    IconAssetDescription,
    MediaAssetDescription,
    StickerAssetDescription,
    TextAssetDescription,
} from '../types';

import { formatAsset } from './assetFormatters';

const colorAssetFormat = (): string => {
    const example: ColorAssetDescription = {
        type: ASSET_TYPES.BACKGROUND,
        name: '<asset_name>',
        bounds: '<x, y, width, height>',
        color: '<color as 24-bit integer>',
    };
    return '\n' + formatAsset(example);
};

const CaptionFormat = (): string => {
    const example: CaptionDescription = {
        type: ASSET_TYPES.CAPTION,
        name: '<asset_name>',
        bounds: '<x, y, max_width, max_height>',
        sceneDesignId: '<the global caption design id applied to this scene group>',
    };
    return '\n' + formatAsset(example);
};

const textAssetFormat = (): string => {
    const example: DocumentationExample<TextAssetDescription> = {
        type: ASSET_TYPES.TEXT,
        name: '<asset_name>',
        bounds: '<x, y, max_width, max_height>',
        text: '<text content>',
        alignment: `<${TextAlignment.LEFT} | ${TextAlignment.CENTER} | ${TextAlignment.RIGHT}>`,
        colors: '<normal and highlight text color as 24-bit integer>',
        lines: '<number of text lines>',
        font: '<font family>',
        fontSize: '<font size>',
    };
    return '\n' + formatAsset(example as TextAssetDescription);
};

const iconAssetFormat = (): string => {
    const example: IconAssetDescription = {
        type: ASSET_TYPES.ICON,
        name: '<asset_name>',
        bounds: '<x, y, width, height>',
        iconColors: '<comma-separated list of colors as 24-bit integers>',
    };
    return '\n' + formatAsset(example);
};

const groupAssetFormat = (): string => {
    const example: GroupAssetDescription = {
        type: ASSET_TYPES.GROUP,
        name: '<asset_name>',
        bounds: '<x, y, width, height>',
        children: '<comma-separated list of child asset names>',
        childrenAlignment: `<${VideoData.LEFT_ALIGNMENT} | ${VideoData.CENTER_ALIGNMENT} | ${VideoData.RIGHT_ALIGNMENT}>`,
    };
    return '\n' + formatAsset(example);
};

const mediaAssetFormat = (): string => {
    const example: DocumentationExample<MediaAssetDescription> = {
        type: ASSET_TYPES.MEDIA,
        name: '<asset_name>',
        bounds: '<x, y, width, height>',
        mediaId: '<media ID>',
        description: '<description>',
        mediaType: '<image/video>',
        position:
            '<x, y> — where the media box sits on the canvas (what set_asset_position moves); distinct from mediaTranslation, which pans the media within its own frame',
        mediaScale: '<scale%>',
        mediaResolution: '<width, height>',
        mediaTranslation: '<x, y>',
        mediaXTranslationRange: '<[min, max]>',
        mediaYTranslationRange: '<[min, max]>',
    };
    return '\n' + formatAsset(example as MediaAssetDescription);
};

const stickerAssetFormat = (): string => {
    const example: StickerAssetDescription = {
        type: ASSET_TYPES.STICKER,
        name: '<asset_name>',
        bounds: '<x, y, width, height>',
        mediaId: '<media ID>',
    };
    return '\n' + formatAsset(example);
};

export const getAssetAnnotationFormat = (): string => {
    return (
        groupAssetFormat() +
        textAssetFormat() +
        iconAssetFormat() +
        mediaAssetFormat() +
        stickerAssetFormat() +
        colorAssetFormat() +
        CaptionFormat()
    );
};

export const getHowOurVideosWorkGuide = (): string => {
    return `## How our videos work

There are certain specifics to how our videos work that you should be aware of:
- Assets on a scene cannot be added or removed, only modified. A scene design is a fixed set of assets in a fixed layout, and what you can do to an asset depends on its type: TEXT — write, clear, and fully restyle; MEDIA — fill or swap the media and reframe it within its frame (scale, crop, Ken Burns, brightness), but the frame itself is fixed; ICON — swap the icon only; BACKGROUND — recolor. You can never add an asset a design doesn't have, remove one, move a media frame, or strip a built-in decorative graphic.
- Text assets with no text set are not displayed in the video.
- Videos must have at least one scene.
- Scene groups can be one of several types: "regular" (no audio — the on-screen text and media must communicate the whole message), "ai_voiceover" (AI-generated voiceover audio), "recorded_voiceover" (a human-recorded voiceover), "ai_talking_head" (an AI avatar speaking), or "recorded_talking_head" (a real person speaking on camera). Check the scene group type to understand how each part of the video communicates its message. A video can combine different group types (e.g. a text title scene, then a voiceover section, then a text outro).
- Scene indexes are 0-based but users are shown 1-based indexes in the UI. When referring to scenes, always use 0-based indexing internally and 1-based indexing when communicating with users.
- "highlighted" text is displayed with "**" around it, indicating that it is rendered in a special way. Remove the "**" when you want to edit or display the text, as it is not part of the actual content.`;
};

export const getVideoAnnotationGuide = (): string => {
    return `## Video State Format
Tool results include the current video state (or a diff of it) serialized in this format:
${getVideoAnnotationFormat()}

Each asset is serialized as:
${getAssetAnnotationFormat()}
`;
};

export const getVideoAnnotationFormat = (): string => {
    return `
- SCENE GROUP <index> groups one or more scenes that share timing (e.g., voiceover, talking head)
  - Types: regular (no audio), ai_voiceover (AI voiceover), recorded_voiceover (human voiceover), ai_talking_head (AI avatar speaker), recorded_talking_head (real person on camera)
  - Contains: sceneIndices (which scenes belong to this group), duration, transcript (word timings for audio-driven groups), and — for AI voiceover groups — the voice and voiceSpeed
  - Transcript includes word-by-word timing (startTime/endTime in ms) for overlay placement decisions
  - A pause (silence) between words appears in the transcript as a \`<pause seconds="N"/>\` tag, where N is its length in seconds and may be fractional for pauses from recorded audio (e.g. \`<pause seconds="2.3"/>\`). Treat the transcript as one stream of tokens where a pause is just another token, like a word — the \`<pause/>\` tag is part of the text you read and part of the text you write. On AI voiceover groups you can add, move, resize, or remove pauses by rewording (edit_ai_voiceover): find_phrase and replacement are token strings and the edit is a literal find-and-replace. Include every \`<pause/>\` that falls inside the run you are replacing in find_phrase (or it won't match). Add a \`<pause/>\` tag in the replacement to insert silence; omit it to remove it; reposition or change its N to move or resize it (N is a whole number of seconds, 1-9).
- SCENE <index> contains assets that define the layout and content
  - Scenes are listed under their scene group's header; each scene belongs to exactly one group
  - Serialized with: design (its scene design id), duration, subscenes (count), covers (voice-driven groups only: the transcript words this scene plays over and its role — overlay, uncovered, or speaker on screen), textPositionPresets, and its assets
- Asset types include:
  - ${ASSET_TYPES.TEXT}: text rendered on the scene with size, max_chars, and editability
  - ${ASSET_TYPES.CAPTION}: the global caption track for a voice-driven scene group, serialized as the \`captions\` asset; content is driven by the transcript (read-only), and its on/off state and design (sceneDesignId) are controlled globally via set_captions
  - ${ASSET_TYPES.FANCY_CAPTION}: a scene-level caption/text element that belongs to the scene design (its own text and styling); distinct from the global \`captions\` track and not controlled by set_captions
  - ${ASSET_TYPES.MEDIA}: image or video displayed on the scene with dimensions and editability
  - ${ASSET_TYPES.ICON}: icon displayed on the scene with dimensions and editability
  - ${ASSET_TYPES.STICKER}: sticker displayed on the scene with dimensions and editability
  - ${ASSET_TYPES.GROUP}: alignment container for child assets (not visible)
  - ${ASSET_TYPES.WATERMARK}: watermark image displayed on the scene
  - ${ASSET_TYPES.BACKGROUND}: solid background color with children`;
};

export const getDiffFormatGuide = (): string => {
    return `## Understanding Video State Diffs

Tools that modify the video automatically return a git-style diff showing what changed. This helps you track the evolution of the video over time.

### Diff Format Symbols
- \`+\` prefix: Property or asset was **added**
- \`-\` prefix: Property or asset was **removed**
- No prefix (context lines): Shows which asset is being modified
- \`@@ Section Name @@\`: Header indicating which part of video changed

### When You See Diffs
- **Initial state**: All properties shown with \`+\` (establishing baseline)
- **After tool execution**: Only changed properties shown with \`-\` (old) and \`+\` (new)
- **Modified assets**: Use shorthand \`asset: name\` to identify which asset changed

**Important**: Every tool that modifies the video will return a diff in this format. Use these diffs to track what changed and build your understanding of the current video state.`;
};
